import { useState, useEffect } from 'react';

interface StableViewportInfo {
  // Basic mobile detection
  isMobile: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isTablet: boolean;
  
  // Safe viewport dimensions
  viewportWidth: number;
  viewportHeight: number;
  availableHeight: number;
  
  // Simple orientation
  orientation: 'portrait' | 'landscape';
  
  // Basic safe areas with CSS fallbacks
  safeAreas: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
}

// Simple, stable device detection
const getDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return {
      isMobile: false,
      isIOS: false,
      isAndroid: false,
      isTablet: false,
      viewportWidth: 1200,
      viewportHeight: 800,
      orientation: 'landscape' as const
    };
  }

  const userAgent = navigator.userAgent;
  const width = window.innerWidth;
  const height = window.innerHeight;
  
  // Simple, reliable device detection
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  const isMobile = width < 768;
  const isTablet = (width >= 768 && width < 1024) || /iPad/.test(userAgent);
  const orientation: 'portrait' | 'landscape' = width > height ? 'landscape' : 'portrait';
  
  return {
    isMobile,
    isIOS,
    isAndroid,
    isTablet,
    viewportWidth: width,
    viewportHeight: height,
    orientation
  };
};

// Universal safe area detection using modern APIs and CSS env() values
const getSafeAreasFallback = (isIOS: boolean): StableViewportInfo['safeAreas'] => {
  if (typeof window === 'undefined') {
    return { top: 0, bottom: 0, left: 0, right: 0 };
  }

  let safeAreas = { top: 0, bottom: 0, left: 0, right: 0 };

  // Method 1: Try visualViewport API first (most accurate for modern browsers)
  if (window.visualViewport) {
    const viewport = window.visualViewport;
    const windowHeight = window.innerHeight;
    const visualHeight = viewport.height;
    
    // Calculate safe areas based on viewport differences
    if (visualHeight < windowHeight) {
      const heightDiff = windowHeight - visualHeight;
      safeAreas.top = Math.max(viewport.offsetTop, 0);
      safeAreas.bottom = Math.max(heightDiff - safeAreas.top, 0);
    }
  }

  // Method 2: CSS env() values using computed styles
  if (CSS.supports('padding: env(safe-area-inset-top)')) {
    const testEl = document.createElement('div');
    testEl.style.cssText = `
      position: fixed;
      top: env(safe-area-inset-top);
      bottom: env(safe-area-inset-bottom);
      left: env(safe-area-inset-left);
      right: env(safe-area-inset-right);
      visibility: hidden;
      pointer-events: none;
    `;
    document.body.appendChild(testEl);
    
    const style = getComputedStyle(testEl);
    const envTop = parseInt(style.top) || 0;
    const envBottom = parseInt(style.bottom) || 0;
    const envLeft = parseInt(style.left) || 0;
    const envRight = parseInt(style.right) || 0;
    
    document.body.removeChild(testEl);
    
    // Use env() values if they're available and greater than our viewport calculation
    safeAreas.top = Math.max(safeAreas.top, envTop);
    safeAreas.bottom = Math.max(safeAreas.bottom, envBottom);
    safeAreas.left = Math.max(safeAreas.left, envLeft);
    safeAreas.right = Math.max(safeAreas.right, envRight);
  }

  // Method 3: Universal iOS fallback (only if no safe areas detected)
  if (isIOS && safeAreas.top === 0 && safeAreas.bottom === 0) {
    // Feature detection instead of device detection
    const hasNotch = window.screen.height > window.screen.width * 1.5; // Rough notch detection
    const isPortrait = window.orientation === 0 || Math.abs(window.orientation) === 180;
    
    if (hasNotch && isPortrait) {
      safeAreas.top = 47; // Standard notch height in portrait
      safeAreas.bottom = 34; // Standard home indicator
    } else if (hasNotch) {
      safeAreas.top = 44; // Standard status bar in landscape
      safeAreas.bottom = 21; // Reduced home indicator in landscape
    } else {
      safeAreas.top = 20; // Standard status bar for older devices
    }
  }
  
  return safeAreas;
};

// @deprecated - Use useCentralizedMobileDetection instead
export const useStableMobileDetection = (): StableViewportInfo => {
  const [viewportInfo, setViewportInfo] = useState<StableViewportInfo>(() => {
    const device = getDeviceInfo();
    const safeAreas = typeof window !== 'undefined' 
      ? getSafeAreasFallback(device.isIOS)
      : { top: 0, bottom: 0, left: 0, right: 0 };
    
    // Universal available height calculation
    const availableHeight = device.isMobile 
      ? Math.max(device.viewportHeight - safeAreas.top - safeAreas.bottom - 10, 400)
      : device.viewportHeight;
    
    // Debug logging for viewport monitoring
    if (device.isIOS && device.isMobile) {
      console.log('📱 Viewport debug:', {
        screen: { width: window.screen.width, height: window.screen.height },
        viewport: { width: device.viewportWidth, height: device.viewportHeight },
        safeAreas,
        availableHeight,
        orientation: device.orientation
      });
    }
    
    return {
      ...device,
      availableHeight,
      safeAreas
    };
  });

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const updateViewport = () => {
      // Debounce updates to prevent excessive re-renders
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const device = getDeviceInfo();
        const safeAreas = getSafeAreasFallback(device.isIOS);
        
        // Universal available height calculation
        const availableHeight = device.isMobile 
          ? Math.max(device.viewportHeight - safeAreas.top - safeAreas.bottom - 10, 400)
          : device.viewportHeight;
        
        // Debug logging for viewport updates
        if (device.isIOS && device.isMobile) {
          console.log('🔄 Viewport update:', {
            viewport: { width: device.viewportWidth, height: device.viewportHeight },
            safeAreas,
            availableHeight
          });
        }
        
        setViewportInfo({
          ...device,
          availableHeight,
          safeAreas
        });
      }, 100);
    };

    // Listen to essential viewport events
    window.addEventListener('resize', updateViewport, { passive: true });
    window.addEventListener('orientationchange', updateViewport, { passive: true });
    
    // Enhanced viewport change detection using visualViewport API
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport, { passive: true });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      
      // Clean up visualViewport listeners
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport);
      }
    };
  }, []);

  return viewportInfo;
};